/**
 * Where the floating "Ask AI" launcher sits, as pure helpers.
 *
 * The position is a person's preference (like theme and density), so it lives
 * in localStorage under `aiAnalyst.launcher` rather than in the URL.
 *
 * The model is `{ edge, offset }`, not pixels, so it survives a resize:
 *
 * - `edge` is the viewport edge the button is parked against, `MARGIN_PX`
 *   away from it.
 * - `offset` is a fraction from 0 to 1 along that edge, measured from the
 *   **bottom** on the left and right edges and from the **right** on the top
 *   and bottom edges. 0 and 1 are the two corners, so a corner stays a corner
 *   at any window size. The default, `{ edge: 'right', offset: 0 }`, is the
 *   bottom-right corner, 24px from each edge.
 *
 * jsdom has no layout, so every pixel decision is here and tested directly.
 */

export type LauncherEdge = 'right' | 'left' | 'top' | 'bottom'

export interface LauncherPosition {
  edge: LauncherEdge
  /** 0..1 along the edge: from the bottom (left/right) or the right (top/bottom). */
  offset: number
}

export interface Size {
  width: number
  height: number
}

export interface Point {
  x: number
  y: number
}

/** Distances from each viewport edge, in px; `null` leaves that side `auto`. */
export interface LauncherOffsets {
  top: number | null
  right: number | null
  bottom: number | null
  left: number | null
}

export const LAUNCHER_STORAGE_KEY = 'aiAnalyst.launcher'

/** A pointer that moves further than this between down and up is a drag, not a click. */
export const DRAG_THRESHOLD_PX = 4

/** The gap between the button and the edge it is parked on. */
export const MARGIN_PX = 24

/** The button's resting diameter (`size-12`). */
export const LAUNCHER_SIZE_PX = 48

/** Released this close to a corner along its edge, the button snaps into the corner. */
export const CORNER_SNAP_PX = 64

export const DEFAULT_LAUNCHER_POSITION: LauncherPosition = { edge: 'right', offset: 0 }

const EDGES: readonly LauncherEdge[] = ['right', 'left', 'top', 'bottom']

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

function isPosition(value: unknown): value is LauncherPosition {
  if (typeof value !== 'object' || value === null) return false
  const { edge, offset } = value as Record<string, unknown>
  return (
    typeof edge === 'string' &&
    EDGES.includes(edge as LauncherEdge) &&
    typeof offset === 'number' &&
    Number.isFinite(offset)
  )
}

/** The saved position, or the default when there is none, it is corrupt, or storage is blocked. */
export function readLauncherPosition(): LauncherPosition {
  try {
    const raw = window.localStorage.getItem(LAUNCHER_STORAGE_KEY)
    if (!raw) return DEFAULT_LAUNCHER_POSITION
    const parsed: unknown = JSON.parse(raw)
    if (!isPosition(parsed)) return DEFAULT_LAUNCHER_POSITION
    return { edge: parsed.edge, offset: clamp01(parsed.offset) }
  } catch {
    // Private mode or blocked storage: the default corner is a fine answer.
    return DEFAULT_LAUNCHER_POSITION
  }
}

/** Fired on `window` when the button is moved here, for what must stay out of its way. */
const MOVED_EVENT = 'aiAnalyst.launcher.moved'

/** Where the button was last put in this page, kept even when storage is blocked. */
let latest: LauncherPosition | null = null

/** Save the position. A blocked storage only costs the preference, so it is not an error. */
export function writeLauncherPosition(position: LauncherPosition): void {
  latest = position
  try {
    window.localStorage.setItem(LAUNCHER_STORAGE_KEY, JSON.stringify(position))
  } catch {
    // Storage is full or blocked; the button still moves for this visit.
  }
  window.dispatchEvent(new Event(MOVED_EVENT))
}

/** Where the button is now: moved in this page, else as saved. */
export function currentLauncherPosition(): LauncherPosition {
  return latest ?? readLauncherPosition()
}

/** Calls `onMove` whenever the button is moved, here or in another tab. */
export function subscribeLauncherPosition(onMove: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === LAUNCHER_STORAGE_KEY) {
      latest = null
      onMove()
    }
  }
  window.addEventListener(MOVED_EVENT, onMove)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(MOVED_EVENT, onMove)
    window.removeEventListener('storage', onStorage)
  }
}

/**
 * Whether the button sits in the bottom-left corner: on the left edge at the
 * bottom, or on the bottom edge at the left (offsets count from the bottom and
 * from the right).
 */
export function inBottomLeftCorner(position: LauncherPosition): boolean {
  return (
    (position.edge === 'left' && position.offset === 0) ||
    (position.edge === 'bottom' && position.offset === 1)
  )
}

/** Whether the pointer travelled far enough between `start` and `end` to be a drag. */
export function isDrag(start: Point, end: Point): boolean {
  return Math.hypot(end.x - start.x, end.y - start.y) > DRAG_THRESHOLD_PX
}

/** The run along an edge the button's near side can travel, in px. */
function travel(length: number, extent: number): number {
  return Math.max(0, length - extent - 2 * MARGIN_PX)
}

/** The fraction along an edge for a near-side distance, snapped to a corner when close. */
function fractionFor(distance: number, run: number): number {
  if (run <= 0) return 0
  const along = distance - MARGIN_PX
  if (along <= CORNER_SNAP_PX) return 0
  if (along >= run - CORNER_SNAP_PX) return 1
  return clamp01(along / run)
}

/**
 * Where a released button parks: against the edge nearest its centre, at the
 * same place along that edge, or in the corner when it was dropped near one.
 * It never parks in the middle of the page.
 */
export function snapToEdge(center: Point, viewport: Size, size: Size): LauncherPosition {
  const distances: Record<LauncherEdge, number> = {
    right: viewport.width - center.x,
    left: center.x,
    top: center.y,
    bottom: viewport.height - center.y,
  }
  const edge = EDGES.reduce((best, next) => (distances[next] < distances[best] ? next : best))
  if (edge === 'left' || edge === 'right') {
    const fromBottom = viewport.height - (center.y + size.height / 2)
    return { edge, offset: fractionFor(fromBottom, travel(viewport.height, size.height)) }
  }
  const fromRight = viewport.width - (center.x + size.width / 2)
  return { edge, offset: fractionFor(fromRight, travel(viewport.width, size.width)) }
}

export type ArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight'

export function isArrowKey(key: string): key is ArrowKey {
  return key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight'
}

/**
 * Alt+Arrow: move to another corner. The current place is read as the corner
 * it is nearest to; the arrow replaces one side of it. A corner is stored on
 * the left or right edge (offset 0 = bottom, 1 = top).
 */
export function moveByArrow(position: LauncherPosition, key: ArrowKey): LauncherPosition {
  const vertical: 'top' | 'bottom' =
    position.edge === 'top' || position.edge === 'bottom'
      ? position.edge
      : position.offset >= 0.5
        ? 'top'
        : 'bottom'
  const horizontal: 'left' | 'right' =
    position.edge === 'left' || position.edge === 'right'
      ? position.edge
      : position.offset >= 0.5
        ? 'left'
        : 'right'
  const nextVertical = key === 'ArrowUp' ? 'top' : key === 'ArrowDown' ? 'bottom' : vertical
  const nextHorizontal = key === 'ArrowLeft' ? 'left' : key === 'ArrowRight' ? 'right' : horizontal
  return { edge: nextHorizontal, offset: nextVertical === 'top' ? 1 : 0 }
}

/**
 * The distances from the viewport edges that put the button at `position`.
 * Anchored to the edge it is parked on and to the corner its offset counts
 * from, so the default needs no layout at all: right 24, bottom 24.
 */
export function positionToOffsets(
  position: LauncherPosition,
  viewport: Size,
  size: Size,
): LauncherOffsets {
  const offset = clamp01(position.offset)
  if (position.edge === 'left' || position.edge === 'right') {
    const bottom = MARGIN_PX + offset * travel(viewport.height, size.height)
    return {
      top: null,
      bottom,
      left: position.edge === 'left' ? MARGIN_PX : null,
      right: position.edge === 'right' ? MARGIN_PX : null,
    }
  }
  const right = MARGIN_PX + offset * travel(viewport.width, size.width)
  return {
    top: position.edge === 'top' ? MARGIN_PX : null,
    bottom: position.edge === 'bottom' ? MARGIN_PX : null,
    left: null,
    right,
  }
}
